import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtINR, deriveRates, RATE_MULTIPLIERS } from "../../lib/constants";
import { toast } from "sonner";

export function RatesAdmin() {
  const [rate, setRate] = useState(null);
  // Only 24K and silver are entered — 22K / 20K / 18K are derived from 24K.
  // Rates are ENTERED per 10 g (one tola) — that is how the trade quotes them.
  // They are STORED per gram, so every price calculation stays unit-correct.
  const [form, setForm] = useState({ gold_24k_10g: "", silver_10g: "", note: "" });
  const perGram24k = (+form.gold_24k_10g || 0) / 10;
  const derived = deriveRates(perGram24k);           // per gram
  const derived10g = {                                 // per 10 g, for display
    gold_22k: derived.gold_22k * 10,
    gold_20k: derived.gold_20k * 10,
    gold_18k: derived.gold_18k * 10,
  };
  useEffect(() => {
    api.get("/api/rates/current").then((r) => {
      setRate(r.data);
      // Stored per gram -> show per 10 g
      setForm({
        gold_24k_10g: r.data?.gold_24k ? +(r.data.gold_24k * 10).toFixed(2) : "",
        silver_10g: r.data?.silver ? +(r.data.silver * 10).toFixed(2) : "",
        note: "",
      });
    });
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (!(+form.gold_24k_10g > 0)) { toast.error("Enter today's 24K price per 10 g"); return; }
    // The derived carats are sent for transparency; the server recalculates
    // them from 24K regardless, so the stored rates can never drift.
    // Convert the per-10 g entry to the per-gram values the system stores.
    const payload = {
      gold_24k: perGram24k,
      ...deriveRates(perGram24k),
      silver: (+form.silver_10g || 0) / 10,
      note: form.note,
    };
    try {
      const { data } = await api.post("/api/crm/rates/update", payload);
      setRate(data);
      toast.success("Rates updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-6 lg:p-8">
      <style>{`
        .no-spinner::-webkit-outer-spin-button,
        .no-spinner::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .no-spinner { -moz-appearance: textfield; }
      `}</style>

      <div className="mb-6">
        <div className="eyebrow">Daily Update</div>
        <h1 className="font-display text-4xl text-maroon">Gold & Silver Rates</h1>
        <p className="text-xs text-ash mt-1">All changes are logged in the audit history with timestamp and updater name.</p>
      </div>
      {rate && (
        <div className="bg-maroon text-pearl p-5 mb-6">
          <div className="eyebrow text-gold-light">Currently live</div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-3 text-sm">
            <div><div className="text-pearl/60 text-[10px]">24K / 10g</div><div className="font-display text-2xl">{fmtINR((rate.gold_24k || 0) * 10)}</div></div>
            <div><div className="text-pearl/60 text-[10px]">22K / 10g</div><div className="font-display text-2xl">{fmtINR((rate.gold_22k || 0) * 10)}</div></div>
            <div><div className="text-pearl/60 text-[10px]">20K / 10g</div><div className="font-display text-2xl">{fmtINR((rate.gold_20k || 0) * 10)}</div></div>
            <div><div className="text-pearl/60 text-[10px]">18K / 10g</div><div className="font-display text-2xl">{fmtINR((rate.gold_18k || 0) * 10)}</div></div>
            <div><div className="text-pearl/60 text-[10px]">Silver / 10g</div><div className="font-display text-2xl">{fmtINR((rate.silver || 0) * 10)}</div></div>
          </div>
          <div className="text-[10px] text-pearl/60 mt-3">Last updated by {rate.updated_by_name} ({rate.updated_by_role}) at {new Date(rate.updated_at).toLocaleString()}</div>
        </div>
      )}
      <form onSubmit={save} className="bg-white border border-line p-6 space-y-3 max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          {[["gold_24k_10g", "24K GOLD"], ["silver_10g", "SILVER"]].map(([k, label]) => (
            <label key={k} className="text-sm">
              <span className="eyebrow text-[10px]">{label} — per 10 g (₹)</span>
              <input type="number" inputMode="decimal" step="0.01" placeholder="Enter rate" className="no-spinner w-full border border-line px-3 py-2 bg-pearl mt-1" value={form[k]} onChange={(e) => setForm({...form, [k]: e.target.value})} data-testid={`rate-${k}`}/>
            </label>
          ))}
        </div>

        {/* 22K / 20K / 18K are calculated from 24K — shown live so the person
            entering the rate can sanity-check before saving. */}
        <div className="border border-line bg-pearl/50 p-4" data-testid="derived-rates">
          <div className="eyebrow text-[10px] mb-2">Calculated automatically from 24K</div>
          <div className="grid grid-cols-3 gap-3">
            {[["gold_22k", "22K", RATE_MULTIPLIERS.gold_22k],
              ["gold_20k", "20K", RATE_MULTIPLIERS.gold_20k],
              ["gold_18k", "18K", RATE_MULTIPLIERS.gold_18k]].map(([k, label, mult]) => (
              <div key={k} data-testid={`derived-${k}`}>
                <div className="text-[10px] text-ash uppercase tracking-widest">{label} · {Math.round(mult * 100)}%</div>
                <div className="font-display text-xl text-maroon">
                  {+form.gold_24k_10g > 0 ? fmtINR(derived10g[k]) : "—"}
                </div>
                <div className="text-[10px] text-ash">per 10 g</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-ash mt-3">
            Enter today's 24K price <b>per 10 g</b> (one tola, GST included) — for example
            <b> 150000</b>. 22K, 20K and 18K are worked out at 92%, 84% and 78% of it.
          </p>
        </div>
        <textarea placeholder="Note (optional)" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} data-testid="rate-note"/>
        <button type="submit" className="btn-maroon" data-testid="update-rates-btn">Update Today's Rates</button>
      </form>
    </div>
  );
}

export function RateHistory() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/api/crm/rates/history").then((r) => setItems(r.data || [])); }, []);

  const RateChange = ({ prev, next }) => {
    const changed = typeof prev === "number" && prev !== next;
    return (
      <div className="text-xs">
        {typeof prev === "number" ? (
          <span className={changed ? "text-ash line-through" : "text-ash"}>{fmtINR(prev)}</span>
        ) : (
          <span className="text-ash">—</span>
        )}
        <span className="mx-1 text-ash">→</span>
        <span className={changed ? "font-semibold text-maroon" : ""}>{fmtINR(next)}</span>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Trust</div>
        <h1 className="font-display text-4xl text-maroon">Rate Audit Log</h1>
        <p className="text-xs text-ash mt-1">Every rate change ever made — who, when, previous rate → updated rate.</p>
      </div>
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["When", "Who", "Role", "24K (prev → new)", "22K (prev → new)", "20K (prev → new)", "18K (prev → new)", "Silver (prev → new)", "Note"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-line/60" data-testid={`audit-${it.id}`}>
                <td className="p-3 text-xs">{new Date(it.created_at).toLocaleString()}</td>
                <td className="p-3">{it.user_name}</td>
                <td className="p-3 text-ash text-xs">{it.user_role}</td>
                <td className="p-3"><RateChange prev={it.previous?.gold_24k} next={it.new?.gold_24k}/></td>
                <td className="p-3"><RateChange prev={it.previous?.gold_22k} next={it.new?.gold_22k}/></td>
                <td className="p-3"><RateChange prev={it.previous?.gold_20k} next={it.new?.gold_20k}/></td>
                <td className="p-3"><RateChange prev={it.previous?.gold_18k} next={it.new?.gold_18k}/></td>
                <td className="p-3"><RateChange prev={it.previous?.silver} next={it.new?.silver}/></td>
                <td className="p-3 text-xs text-ash">{it.new?.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No rate changes yet.</div>}
      </div>
    </div>
  );
}
